import { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button, Card, Divider, Form, Icon, Modal } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import actions from "actions";
import api from "api";
import { useAppDispatch } from "store";
import { useUserReload } from "utils/useUserReload";
import util from "utils/utils";

type INewChallengeModalWrapper = {
  newChallengeOpen: boolean;
  onCloseModal: () => void;
};

const NewModal = ({
  newChallengeOpen,
  onCloseModal,
}: INewChallengeModalWrapper) => {
  const [creatingChallengeType, setCreatingChallengeType] = useState<
    "" | "manual"
  >("");
  const [name, setName] = useState<string>("");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const reloadUser = useUserReload();

  const createChallenge = useCallback(() => {
    setIsLoading(true);
    api.challenges.create(
      name,
      ({ _id }) => {
        setIsLoading(false);
        reloadUser(() => {
          dispatch(actions.user.onFollow("challenge", _id));
          navigate(`/challenges/${_id}/settings`);
        });
      },
      (err) => {
        toast.error(err.message);
        setIsLoading(false);
      },
    );
  }, [name, navigate, dispatch, reloadUser]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent | React.KeyboardEvent<HTMLInputElement>) => {
      if (e.keyCode === 13 && newChallengeOpen && name !== "") {
        createChallenge();
        e.stopPropagation();
        e.preventDefault();
        return true;
      }
    },
    [createChallenge, name, newChallengeOpen],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, false);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleCloseModal = useCallback(() => {
    setCreatingChallengeType("");
    setName("");
    onCloseModal();
  }, [onCloseModal]);

  return (
    <Modal
      mountNode={document.getElementById("semantic-modal-mount-node")}
      open={newChallengeOpen}
      onOpen={() => setCreatingChallengeType("")}
      onClose={handleCloseModal}
    >
      <Modal.Header>Create {t("generic.challenge")}</Modal.Header>
      {!creatingChallengeType ?
        <Modal.Content>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-evenly",
            }}
          >
            <Card
              fluid
              header={
                <Card.Header style={{ display: "flex", alignItems: "center" }}>
                  <Icon name="magic" />
                  Guided challenge build
                </Card.Header>
              }
              description={`${util.appName()} will help you through the build process, getting you to a start point you can work from.`}
              onClick={() => navigate("/challenges/new/genai")}
              style={{ margin: "0 20px 0 0" }}
            />
            <Divider vertical content="Or" style={{ height: "50px" }} />
            <Card
              fluid
              header={
                <Card.Header>
                  <Icon name="edit" />
                  Start from scratch
                </Card.Header>
              }
              description={`Start building from a blank ${t("generic.challenge")}.`}
              onClick={() => setCreatingChallengeType("manual")}
              style={{ margin: "0 0 0 20px" }}
            />
          </div>
        </Modal.Content>
      : creatingChallengeType === "manual" ?
        <Modal.Content>
          <p>
            What would you like to call your {t("generic.challenge")}? You can
            always change this later.
          </p>
          <Form style={{ marginBottom: 10 }}>
            <Form.Field
              control={Input}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                handleKeyDown(e)
              }
              placeholder={`${t("common:capitalise", { key: "generic.challenge" })} name...`}
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setName(e.target.value)
              }
            />
          </Form>
        </Modal.Content>
      : null}

      <Modal.Actions>
        <Button
          content={t("generic.cancel")}
          onClick={() => {
            handleCloseModal();
          }}
        />
        {creatingChallengeType === "manual" && (
          <Button
            icon="check"
            content={`Create ${t("generic.challenge")}`}
            primary
            onClick={() => createChallenge()}
            loading={isLoading}
            disabled={isLoading || name.trim() === ""}
          />
        )}
      </Modal.Actions>
    </Modal>
  );
};

export default NewModal;
